// noinspection JSUnusedGlobalSymbols

import PouchDB from 'pouchdb';
import PouchDBFind from 'pouchdb-find';
//import PouchDBMemory from 'pouchdb-adapter-memory';
import PouchDBUpsert from 'pouchdb-upsert';
import deepMerge from "deepmerge";
import _ from 'lodash';

import {$store, $e} from '@isudio/globals';
import {config} from '@isudio/config';
import {refreshToken} from '@isudio/core';
import {type IDatabases, type IDatabase, Database, DbTypes, designDocs} from '../';

export class DataService implements IDatabases {
  //[key: string]: any;

  public dataAvailable: boolean = false;
  private databases = new Map<string, IDatabase>();

  constructor() {
  }

  get dbApp(): PouchDB.Database {
    return this.databases.get('dbApp').db;
  }

  get dbLocal(): PouchDB.Database {
    return this.databases.get('dbLocal').db;
  }

  get dbMemory(): PouchDB.Database {
    return this.databases.get('dbMemory').db;
  }

  async setup() {
    _.logInfo(`Setting up database services..`);
    await this.setupDbs();
  }

  isDataAvailable(): boolean {
    return (this.dataAvailable);
  }

  hasDb(name: string): boolean {
    return (this.databases.has(name));
  }

  public async getDbs() {
    const databases = Array.from(this.databases.values());
    return databases.map(db => _.omit(db, 'db'));
  }

  public async getDb(name: string) {
    const database = this.databases.get(name);
    if (!database) throw new Error(`Database ${name} not found.`);

    return _.omit(database, 'db');
  }

  public async setupDbs(dbConfig: any = config.db) {
    const databases = _.compact(_.concat(_.filter(dbConfig.remote?.databases, db => ((db.connect) && (db.type === DbTypes.remote))),
      _.filter(dbConfig.local?.indexeddb?.databases, db => ((db.create) && (db.type === DbTypes.indexeddb))),
      _.filter(dbConfig.local?.memory?.databases, db => ((db.create) && (db.type === DbTypes.memory)))));
    const dbNames = _.map(databases, m => m.name);
    const existingDbs = new Set<string>();
    this.databases.forEach(db => existingDbs.add(db.name));
    const newDbs = dbNames.filter(dbName => !existingDbs.has(dbName));
    const _databases = _.filter(databases, db => newDbs.indexOf(db.name) > -1);

    if (_.get(_databases, 'length') > 0) {
      //_.logDebug(`Setting up ${_.join(newDbs, ', ')} databases..}`);
      await this.createDbs(dbConfig.remote.connections, _databases);

      if (dbConfig.changeFeed) {
        if (dbConfig.changeFeed.startAll) {
          await this.startAllChangeFeeds(dbConfig.changeFeed.configurations);
        } else {
          await this.startChangeFeed(_.find(dbConfig.changeFeed.configurations, {key: dbConfig.changeFeed.startConfiguration}));
        }
      }

      if (dbConfig.replication) {
        if (dbConfig.replication.startAll) {
          await this.startAllReplications(dbConfig.replication.configurations);
        } else {
          await this.startReplication(_.find(dbConfig.replication.configurations, {key: dbConfig.replication.startConfiguration}));
        }
      }
    }

    const data = await this.getDbs();

    if (_.get(_databases, 'length') > 0) {
      this.dataAvailable = true;
      $e.$b($e.events.dbReady, true);
      _.logInfo(`Database services setup completed.`);
    }

    return data;
  }

  public async startAllChangeFeeds(changeFeeds: Array<any>) {
    if (_.get(changeFeeds, 'length') > 0) {
      const _changeFeeds = _.filter(changeFeeds, r => r.enabled);
      for (let r = 0; r < _changeFeeds.length; r++) {
        await this.startChangeFeed(_changeFeeds[r]);
      }
    }
  }

  public async startChangeFeed(config: any): Promise<void> {
    $store.Db.feed[config.key] = config;
    const database = this.databases.get(config.dbName);
    const dbFeed = _.get(database, 'db');

    if (config?.options?.query_params) {
      config.options.query_params = _.mapValues(config.options.query_params, (value: any) => {
        return (_.startsWith(value, "$store.") ? _.get($store, value.replace("$store.", "")) : value)
      })
    }

    return await this.changeFeed(config.key, dbFeed, config.options).then(() => {
      _.logDebug(`${_.capitalize(_.toLower(config.title))} change feed for ${database.name} database started.`);

    }).then(async () => {
      $e.$b($e.events[`${config.key}FeedStarted`]);
    });
  }

  public async startAllReplications(replications: Array<any>) {
    if (_.get(replications, 'length') > 0) {
      const _replications = _.filter(replications, r => r.enabled);
      for (let r = 0; r < _replications.length; r++) {
        await this.startReplication(_replications[r]);
      }
    }
  }

  public async startReplication(_config: any, dbConfig: any = config.db): Promise<void> {
    const config = _config.continuous && !_config.options.live && !_config.options.retry ? _.cloneDeep(_config) : $store.Db.sync[_config.key];
    if (_config.continuous && !_config.options.live && !_config.options.retry) $store.Db.sync[config.key] = config;
    const source = this.databases.get(config.source);
    const target = this.databases.get(config.target);
    const sourceDb = _.get(source, 'db');
    const targetDb = _.get(target, 'db');
    const method = config.sync ? this.sync : this.replicate;
    const methodName = config.sync ? 'sync' : 'replication';

    if (config?.options?.query_params) {
      config.options.query_params = _.mapValues(config.options.query_params, (value: any) => {
        return (_.startsWith(value, "$store.") ? _.get($store, value.replace("$store.", "")) : value)
      })
    }

    return await method(config.key, sourceDb, targetDb, config.options).then(() => {
      if (!config.options.live && !config.options.retry) {
        _.logDebug(`${_.capitalize(_.toLower(config.title))} ${methodName} ${config.sync ? 'between' : 'from'} ${source.name} ${config.sync ? 'and' : 'to'} ${target.name} database completed.`);
      } else {
        _.logDebug(`Continuous ${config.title} ${methodName} ${config.sync ? 'between' : 'from'} ${source.name} ${config.sync ? 'and' : 'to'} ${target.name} database is enabled.}`);
      }

    }).then(async () => {
      if (config.continuous && !config.options.live && !config.options.retry) {
        config.options.live = true;
        config.options.retry = true;
        config.options.since = 'now';
        $store.Db.sync[config.key] = config;
        await this.startReplication(config);
        if (config?.onComplete) {
          await this.startReplication(_.find(dbConfig.replication.configurations, {key: config.onComplete}));
        }
        $e.$b($e.events[`${config.key}Synced`]);
      }
    });
  }

  public async changeFeed(key: string, database: PouchDB.Database, options: PouchDB.Core.ChangesOptions): Promise<void> {
    $store.Db.feed[key].handle = database.changes(options)
      .on('change', DataService.onFeedChange)
      .on('error', DataService.onFeedError);
  }

  public async replicate(key: string, sourceDb: PouchDB.Database, targetDb: PouchDB.Database, options: PouchDB.Replication.ReplicateOptions): Promise<void> {
    return new Promise(async (resolve) => {
      if (options.live && options.retry) {
        $store.Db.sync[key].handle = sourceDb.replicate.to(targetDb, options)
          .on('change', DataService.onSyncChange)
          .on('paused', DataService.onSyncPaused)
          .on('denied', DataService.onSyncDenied)
          .on('error', DataService.onSyncError);

        resolve();

      } else {
        $store.Db.sync[key].handle = sourceDb.replicate.to(targetDb, options)
          .on('complete', () => resolve())
          .on('change', DataService.onSyncChange)
          .on('paused', DataService.onSyncPaused)
          .on('denied', DataService.onSyncDenied)
          .on('error', DataService.onSyncError);
      }
    });
  }

  public async sync(key: string, sourceDb: PouchDB.Database, targetDb: PouchDB.Database, options: PouchDB.Replication.ReplicateOptions): Promise<void> {
    return new Promise(async (resolve) => {
      if (options.live && options.retry) {
        $store.Db.sync[key].handle = sourceDb.sync(targetDb, options)
          .on('change', DataService.onSyncChange)
          .on('paused', DataService.onSyncPaused)
          .on('denied', DataService.onSyncDenied)
          .on('error', DataService.onSyncError);

        resolve();

      } else {
        $store.Db.sync[key].handle = sourceDb.sync(targetDb, options)
          .on('complete', () => resolve())
          .on('change', DataService.onSyncChange)
          .on('paused', DataService.onSyncPaused)
          .on('denied', DataService.onSyncDenied)
          .on('error', DataService.onSyncError);
      }
    });
  }

  public async loadDocs(sourceDb: PouchDB.Database, targetDb: PouchDB.Database, options: PouchDB.Core.AllDocsWithKeyOptions | PouchDB.Core.AllDocsWithKeysOptions | PouchDB.Core.AllDocsWithinRangeOptions | PouchDB.Core.AllDocsOptions = {}, includeDocs: boolean = true, noRevision: boolean = true): Promise<any> {
    const targetDbName = targetDb.name.substring(targetDb.name.lastIndexOf('/') + 1);
    if (includeDocs) options = _.merge(options, {include_docs: true});
    const res = await sourceDb.allDocs(options);
    const dbDocs = res.rows.map(row => (noRevision ? _.omit(row.doc, '_rev') : row.doc));
    await targetDb.bulkDocs(dbDocs).then(() => _.logDebug(`${dbDocs.length} documents from $sourceDbName} loaded into ${targetDbName} database.`));
    return dbDocs;
  }

  public async removeDbs() {
    this.databases.forEach((d) => {
      if (d.type === DbTypes.remote) {
        d.db.close();
      } else {
        d.db.destroy();
      }
      this.databases.delete(d.name);
    });
  }

  private async createDbs(connections: Array<any>, databases: Array<any>) {
    PouchDB.plugin(PouchDBUpsert);
    PouchDB.plugin(PouchDBFind);
    //PouchDB.plugin(PouchDBMemory);

    const remote = _.filter(databases, db => (db.type === DbTypes.remote));
    let token = _.get($store, "User.session.accessToken.token");
    if (_.get(remote, 'length') > 0) {
      const _connections = _.map(connections, (c: any) => {
        const url = `${c.protocol}${c.host}${c.port ? ':' + c.port : ''}`;

        _.set(c, 'options.fetch', async (url: RequestInfo, opts: RequestInit) => {
          const addAuthHeader = (opts: RequestInit, token: string) => {
            const headers = new Headers(opts.headers || {});
            headers.set("Authorization", `Bearer ${token}`);
            headers.set("Accept", "*/*");
            headers.set("Content-Type", "application/json");
            opts.headers = headers;
            opts.credentials = "include";
            opts.cache = "no-cache";
            opts.redirect = "follow";
            opts.referrerPolicy = "no-referrer";
          };

          addAuthHeader(opts, token);

          let response: Response;

          try {
            response = await PouchDB.fetch(url, opts);

          } catch (err) {
            _.logError(err);
            throw err;
          }

          if (response.status === 401) {
            _.logDebug("PouchDB request was unauthorized. Attempting to refresh access token...");

            try {
              const newAccessToken = await refreshToken();
              if (newAccessToken) {
                token = newAccessToken;
                addAuthHeader(opts, token);

                response = await PouchDB.fetch(url, opts);

              } else {
                _.logError("Access token refresh failed.");
              }

            } catch (err) {
              _.logError(err);
              throw err;
            }
          }

          return response;
        });

        return {
          ...c,
          url
        };
      });

      for (let rdb = 0; rdb < remote.length; rdb++) {
        const d: any = remote[rdb];
        const connection = _.find(_connections, {name: d.connection});
        const url = `${_.get(connection, 'url')}/${d.dbName}`;
        const db = new PouchDB(`${url}`, _.cloneDeep(connection.options));
        await db.info().then(async () => {
          const conn = _.cloneDeep(connection);
          _.set(conn, 'url', url);
          this.databases.set(d.name, (_.omitBy(_.omit(new Database({
            name: d.name,
            dbName: d.dbName,
            type: DbTypes.remote,
            db: db,
            connection: conn,
            writable: d.writable,
            searchUrl: (_.has(d, 'searchUrl') ? `${conn.url}${d.searchUrl}` : null)
          }), ['data']), _.isNil) as Database));
          _.set(this, d.name, {
            ...db,
            searchUrl: (_.has(d, 'searchUrl') ? `${conn.url}${d.searchUrl}` : null)
          });
          //_.logDebug(`Completed setting up ${d.name} database.`);
        });
      }
    }

    const indexeddb = _.filter(databases, db => (db.type === DbTypes.indexeddb));
    if (_.get(indexeddb, 'length') > 0) {
      for (let idb = 0; idb < indexeddb.length; idb++) {
        const d: any = indexeddb[idb];
        const db = new PouchDB(d.dbName, {adapter: 'idb'});
        await db.info().then(async () => {
          if (d.compact) await db.compact();
          if (_.get(d, 'indexes.length') > 0) {
            for (let idx = 0; idx < d.indexes.length; idx++) {
              await db.get(`_design/${d.indexes[idx].ddoc}`).then(async (doc: any) => {
                if (!_.isEqual(_.keys(doc.views.index.map.fields), d.indexes[idx].fields)) {
                  await db.remove(doc);
                }
              }).catch(e => e);
              await db.createIndex(d.indexes[idx]).catch(_.logError);
            }
          }
          if (_.get(d, 'designs.length') > 0) {
            for (let dsn = 0; dsn < d.designs.length; dsn++) {
              const design = d.designs[dsn];
              const dDocConfig = _.find(designDocs, {name: design});
              const dDoc = this.normalizeDoc(_.get(dDocConfig, `designDoc.${dDocConfig.docId}`), `_design/${dDocConfig.docId}`);
              await db.upsert(`_design/${dDocConfig.docId}`, (_dDoc: any) => {
                if (!_.isEqual(_.omit(_dDoc, ['_id', '_rev']), _.omit(dDoc, ['_id', '_rev']))) {
                  if (_.has(_dDoc, '_rev')) _.set(dDoc, '_rev', _dDoc._rev);
                  return dDoc;
                } else {
                  return false;
                }

              }).then(() => {
                $store.App.dbReady = true;
              });
            }
          }

          this.databases.set(d.name, (_.omitBy(_.omit(new Database({
            name: d.name,
            dbName: d.dbName,
            type: DbTypes.indexeddb,
            db: db
          }), ['data']), _.isNil) as Database));
          _.set(this, d.name, db);
          //_.logDebug(`Completed setting up ${d.name} database.`);
        });
      }
    }

    const memory = _.filter(databases, db => (db.type === DbTypes.memory));
    if (_.get(memory, 'length') > 0) {
      for (let mem = 0; mem < memory.length; mem++) {
        const d: any = memory[mem];
        const db = new PouchDB(d.dbName, {adapter: 'memory'});
        await db.info().then(async () => {
          if (_.get(d, 'indexes.length') > 0) {
            for (let idx = 0; idx < d.indexes.length; idx++) {
              await db.createIndex(d.indexes[idx]);
            }
          }

          this.databases.set(d.name, (_.omitBy(_.omit(new Database({
            name: d.name,
            dbName: d.dbName,
            type: DbTypes.memory,
            db: db
          }), ['data']), _.isNil) as Database));
          _.set(this, d.name, db);
          //_.logDebug(`Completed setting up ${d.name} database.`);
        });
      }
    }
    this.setProperties();
  }

  private normalizeDoc(doc: any, id: string) {
    const normalize = (doc: any) => {
      doc = _.extend({}, doc);
      _.keys(doc).forEach((prop) => {
        let type = typeof doc[prop];
        if (type === 'object') {
          doc[prop] = normalize(doc[prop]);
        } else if (type === 'function') {
          doc[prop] = doc[prop].toString();
        }
      });
      return doc;
    }

    let output = normalize(doc);
    output._id = id || doc._id;
    return output;
  }

  private setProperties() {
    this.databases.forEach((d: IDatabase) => {
      _.set(d.db, 'getDoc', async function (
        this: PouchDB.Database,
        id: string, useFind = false
      ): Promise<PouchDB.Core.Document<any> | null> {
        try {
          let doc = null;

          if (!useFind) {
            doc = await this.get(id);

          } else {
            const response = await this.find({
              selector: {id: {$eq: id}}
            });

            if (response.docs && response.docs[0]) {
              doc = response.docs[0];
            }
          }

          return doc;

        } catch (error) {
          return null;
        }
      }.bind(d.db));

      _.set(d.db, 'createDoc', async function (
        this: PouchDB.Database,
        doc: PouchDB.Core.Document<any>
      ): Promise<boolean> {
        try {
          await this.upsert(doc._id, (d: any) => {
            return (_.isNothing(d) ? doc : false);

          }).then((res: PouchDB.UpsertResponse) => {
            return res.updated;

          }).catch(() => {
            return false;
          });

        } catch (error) {
          return false;
        }
      }.bind(d.db));

      _.set(d.db, 'updateDoc', async function (
        this: PouchDB.Database,
        doc: PouchDB.Core.Document<any>
      ): Promise<boolean> {
        try {
          await this.upsert(doc._id, (d: any) => {
            return (_.isNothing(d) ? false : doc);

          }).then((res: PouchDB.UpsertResponse) => {
            return res.updated;

          }).catch(() => {
            return false;
          });

        } catch (error) {
          return false;
        }
      }.bind(d.db));

      _.set(d.db, 'replaceDoc', async function (
        this: PouchDB.Database,
        doc: PouchDB.Core.Document<any>
      ): Promise<boolean> {
        try {
          await this.upsert(doc._id, (d: any) => {
            return ((!_.isEqual(_.omit(doc, ['_rev']), _.omit(d, ['_rev']))) ? doc : false);

          }).then((res: PouchDB.UpsertResponse) => {
            return res.updated;

          }).catch(() => {
            return false;
          });

        } catch (error) {
          return false;
        }
      }.bind(d.db));

      _.set(d.db, 'mergeDoc', async function (
        this: PouchDB.Database,
        doc: PouchDB.Core.Document<any>
      ): Promise<boolean> {
        try {
          await this.upsert(doc._id, (d: any) => {
            if (!_.isNothing(d)) {
              return deepMerge(_.omit(d, '_rev'), _.omit(doc, '_rev'), {
                arrayMerge: (destination: any, source: any) => {
                  return _.uniqWith(_.union(destination, source), _.isEqual)
                }
              });

            } else {
              return doc;
            }

          }).then((res: PouchDB.UpsertResponse) => {
            return res.updated;

          }).catch(() => {
            return false;
          });

        } catch (error) {
          return false;
        }
      }.bind(d.db));

      if (_.isUserAdmin()) {
        _.set(d.db, 'getAll', async function (
          this: PouchDB.Database,
          options: PouchDB.Core.AllDocsWithKeyOptions | PouchDB.Core.AllDocsWithKeysOptions | PouchDB.Core.AllDocsWithinRangeOptions | PouchDB.Core.AllDocsOptions = {},
          includeDocs: boolean = true,
          noRevision: boolean = false
        ): Promise<Array<PouchDB.Core.Document<any>>> {
          if (includeDocs) options = _.merge(options, {include_docs: true});
          const res = await this.allDocs(options);
          return res.rows.map((row: any) => (noRevision ? _.omit(row.doc, '_rev') : row.doc));
        }.bind(d.db));

        _.set(d.db, 'deleteDoc', async function (
          this: PouchDB.Database,
          id: string
        ): Promise<boolean> {
          try {
            await this.upsert(id, (doc: any) => {
              if (!_.isNothing(doc)) {
                _.set(doc, '_deleted', true);
                return doc;
              } else {
                return false;
              }

            }).then((res: PouchDB.UpsertResponse) => {
              return res.updated;

            }).catch(() => {
              return false;
            });

          } catch (error) {
            return false;
          }
        }.bind(d.db));

        _.set(d.db, 'removeDoc', async function (
          this: PouchDB.Database,
          id: string,
          useFind = false
        ): Promise<boolean> {
          try {
            let doc = null;

            if (!useFind) {
              doc = await this.get(id);

            } else {
              const response = await this.find({
                selector: {id: {$eq: id}}
              });

              if (response.docs && response.docs[0]) {
                doc = response.docs[0];
              }
            }

            if (!_.isNothing(doc)) {
              await this.remove(doc);
              return true;
            }
            return false;

          } catch (error) {
            return false;
          }
        }.bind(d.db));

        _.set(d.db, 'bulkUpdate', async function (
          this: PouchDB.Database,
          docs: Array<PouchDB.Core.Document<any>>,
          options: PouchDB.Core.BulkDocsOptions = {
            new_edits: true
          },
          keys?: Array<string>
        ): Promise<(PouchDB.Core.Response | PouchDB.Core.Error)[] | PouchDB.Core.AllDocsWithKeysResponse<{}>> {
          return await this.bulkDocs(docs, options).then(async (res: (PouchDB.Core.Response | PouchDB.Core.Error)[]) => {
            if (!_.isNothing(keys)) {
              return await this.allDocs({
                keys: keys
              });

            } else {
              return res;
            }
          });
        }.bind(d.db));

        _.set(d.db, 'chunkUpdate', async function (
          this: PouchDB.Database,
          docs: Array<PouchDB.Core.Document<any>>,
          chunkSize: number = 1000,
          options: PouchDB.Core.BulkDocsOptions = {
            new_edits: true
          }
        ): Promise<(PouchDB.Core.Response | PouchDB.Core.Error)[] | PouchDB.Core.AllDocsWithKeysResponse<{}>> {
          const chunks = _.chunk(docs, chunkSize);
          return await _.asyncMap(chunks, async (c: any) => {
            return await this['bulkUpdate'](c, options);
          });
        }.bind(d.db));
      }
    });
  }

  private static onFeedChange(data: any) {
    _.logDebug(DataService.onFeedChange.name, data);
  }

  private static onFeedError(err: any) {
    if (!_.isNothing(err)) {
      _.logError('An error occurred while during database change feed!', err);
    }
  }

  private static onSyncChange(data: any) {
    _.logDebug(DataService.onSyncChange.name, data);
  }

  private static onSyncPaused(err: any) {
    if (!_.isNothing(err)) {
      _.logWarning('Sync has paused with error!', err);
    }
  }

  private static onSyncDenied(err: any) {
    if (!_.isNothing(err)) {
      _.logWarning('Sync denied with error!', err);
    }
  }

  private static onSyncError(err: any) {
    if (!_.isNothing(err)) {
      _.logError('An error occurred while syncing the database!', err);
    }
  }
}